"use client";

import { SafeHydrate } from "@/SafeHydrate";
import React, { useState } from "react";
import { Check, Clock, Calendar as CalendarIcon, ArrowRight, MessageSquare, Signal, Battery, Wifi } from "lucide-react";
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
import { Calendar } from "@/components/ui/calendar";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
export default function AutomatedScheduling() {
  const {
    ref,
    isVisible
  } = useRevealOnScroll();
  const [date, setDate] = useState<Date | undefined>(new Date());
  const benefits = [{
    title: "Real-Time Calendar Sync",
    description: "Instantly checks availability across Google, Outlook, and iCloud calendars to prevent double-booking."
  }, {
    title: "No-Show Reduction",
    description: "Automated SMS and email reminders ensure prospects show up for their scheduled appointments."
  }, {
    title: "24/7 Booking Agent",
    description: "Your AI Concierge is always awake, ready to capture leads and book meetings day or night."
  }, {
    title: "Smart Qualification",
    description: "Only books appointments with qualified leads that meet your specific business criteria."
  }];
  return <section id="automated-scheduling" ref={ref} className="dark bg-background text-foreground py-24 relative overflow-hidden">
      {}
      <div className="absolute top-0 left-0 w-full h-full overflow-hidden pointer-events-none">
        <div className="absolute top-1/4 left-[-10%] w-[500px] h-[500px] bg-primary/10 rounded-full blur-[120px]" />
        <div className="absolute bottom-0 right-[-10%] w-[600px] h-[600px] bg-blue-600/10 rounded-full blur-[120px]" />
      </div>

      <div className="container mx-auto px-4 md:px-6 relative z-10">
        <div className={`grid grid-cols-1 lg:grid-cols-2 gap-16 items-center transition-all duration-1000 ${isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-10"}`}>
          
          {}
          <div className="space-y-8">
            <div className="space-y-4">
              <Badge variant="outline" className="w-fit text-primary border-primary/30 bg-primary/10 px-3 py-1">
                <SafeHydrate><CalendarIcon className="w-3 h-3 mr-2" /></SafeHydrate>
                Smart Scheduling
              </Badge>
              <h2 className="text-4xl md:text-5xl font-bold tracking-tight text-white leading-tight">
                Turn Conversations into <span className="text-primary">Confirmed Meetings</span>
              </h2>
              <p className="text-lg text-muted-foreground max-w-xl leading-relaxed">
                Stop playing phone tag. MollyCall's AI agents handle the entire booking process naturally over the phone, syncing directly with your team's calendar in real-time.
              </p>
            </div>

            <div className="space-y-6">
              {benefits.map((benefit, index) => <div key={index} className="flex items-start gap-4 group" data-index={index}>
                  <div className="mt-1 flex-shrink-0 w-6 h-6 rounded-full bg-primary/20 flex items-center justify-center text-primary group-hover:bg-primary group-hover:text-white transition-colors duration-300">
                    <Check className="w-4 h-4" />
                  </div>
                  <div>
                    <h3 className="font-semibold text-white text-lg">{benefit.title}</h3>
                    <p className="text-muted-foreground text-sm mt-1">{benefit.description}</p>
                  </div>
                </div>)}
            </div>

            <div className="pt-4">
              <Link to="/request-demo">
                <Button size="lg" className="rounded-full px-8 h-12 text-base shadow-[0_0_20px_rgba(59,130,246,0.3)] hover:shadow-[0_0_30px_rgba(59,130,246,0.5)] transition-all">
                  Request a Demo <ArrowRight className="ml-2 w-4 h-4" />
                </Button>
              </Link>
            </div>
          </div>

          {}
          <div className="relative flex justify-center lg:justify-end">
            {}
            <div className="relative w-[340px] md:w-[380px] h-[720px] bg-gray-900 rounded-[3rem] border-[8px] border-gray-800 shadow-2xl overflow-hidden ring-1 ring-white/10">
              
              {}
              <div className="absolute top-0 inset-x-0 h-8 bg-black/80 backdrop-blur-md z-20 flex items-center justify-between px-6 text-[10px] font-medium text-white">
                <span>9:41</span>
                <div className="flex items-center gap-1.5">
                  <Signal className="w-3 h-3" />
                  <Wifi className="w-3 h-3" />
                  <Battery className="w-3 h-3" />
                </div>
              </div>

              {}
              <div className="absolute top-2 left-1/2 -translate-x-1/2 w-24 h-6 bg-black rounded-full z-30" />

              {}
              <div className="absolute top-0 inset-x-0 h-24 bg-background/95 backdrop-blur-sm border-b border-border/40 z-10 flex flex-col justify-end pb-3 px-4">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-full bg-gradient-to-br from-primary to-blue-600 flex items-center justify-center text-white shadow-lg">
                    <MessageSquare className="w-5 h-5" />
                  </div>
                  <div>
                    <h4 className="font-semibold text-sm text-white">MollyCall Concierge</h4>
                    <span className="text-xs text-primary flex items-center gap-1">
                      <span className="w-1.5 h-1.5 rounded-full bg-green-500 animate-pulse" />
                      Online
                    </span>
                  </div>
                </div>
              </div>

              {}
              <div className="absolute inset-0 pt-28 pb-20 px-4 overflow-y-auto bg-gradient-to-b from-background to-background/95 scrollbar-hide">
                <div className="space-y-4">
                  
                  {}
                  <div className="flex items-end gap-2">
                    <div className="w-6 h-6 rounded-full bg-primary/20 flex items-center justify-center text-primary text-[10px]">AI</div>
                    <div className="bg-secondary/50 text-secondary-foreground p-3 rounded-2xl rounded-bl-none max-w-[85%] text-sm border border-white/5">
                      Hi there! I noticed you're interested in our enterprise plan. Would you like to schedule a quick demo?
                    </div>
                  </div>

                  {}
                  <div className="flex items-end gap-2 justify-end">
                    <div className="bg-primary text-primary-foreground p-3 rounded-2xl rounded-br-none max-w-[85%] text-sm shadow-md">
                      Yes, that sounds great. What times are available?
                    </div>
                  </div>

                  {}
                  <div className="flex items-end gap-2">
                    <div className="w-6 h-6 rounded-full bg-primary/20 flex items-center justify-center text-primary text-[10px]">AI</div>
                    <div className="bg-secondary/50 text-secondary-foreground p-1 rounded-2xl rounded-bl-none max-w-[95%] border border-white/5 overflow-hidden">
                      <div className="p-3 pb-1">
                        <p className="text-sm mb-2">Please select a date that works for you:</p>
                      </div>
                      <div className="bg-background rounded-xl p-2 shadow-inner">
                        <SafeHydrate><Calendar mode="single" selected={date} onSelect={setDate} className="rounded-md border-none w-full" classNames={{
                          head_cell: "text-muted-foreground font-normal text-[0.8rem] w-8",
                          cell: "h-8 w-8 text-center text-sm p-0 relative [&:has([aria-selected])]:bg-primary/20 first:[&:has([aria-selected])]:rounded-l-md last:[&:has([aria-selected])]:rounded-r-md focus-within:relative focus-within:z-20",
                          day: "h-8 w-8 p-0 font-normal aria-selected:opacity-100 hover:bg-primary/20 rounded-md transition-colors",
                          day_selected: "bg-primary text-primary-foreground hover:bg-primary hover:text-primary-foreground focus:bg-primary focus:text-primary-foreground",
                          day_today: "bg-accent text-accent-foreground"
                        }} /></SafeHydrate>
                      </div>
                      <div className="p-2">
                        <Button size="sm" className="w-full text-xs h-8 bg-primary/90 hover:bg-primary">
                          Confirm Date
                        </Button>
                      </div>
                    </div>
                  </div>

                  {}
                  <div className="flex items-end gap-2">
                    <div className="w-6 h-6 rounded-full bg-primary/20 flex items-center justify-center text-primary text-[10px]">AI</div>
                    <div className="bg-secondary/50 p-3 rounded-2xl rounded-bl-none w-16 flex items-center justify-center gap-1">
                      <span className="w-1.5 h-1.5 bg-muted-foreground/40 rounded-full animate-bounce [animation-delay:-0.3s]" />
                      <span className="w-1.5 h-1.5 bg-muted-foreground/40 rounded-full animate-bounce [animation-delay:-0.15s]" />
                      <span className="w-1.5 h-1.5 bg-muted-foreground/40 rounded-full animate-bounce" />
                    </div>
                  </div>

                </div>
              </div>

              {}
              <div className="absolute bottom-0 inset-x-0 h-20 bg-background border-t border-border/40 px-4 pt-3 pb-6 z-20">
                <div className="flex items-center gap-2">
                  <div className="h-10 flex-1 bg-secondary/30 rounded-full border border-white/5 px-4 flex items-center text-muted-foreground text-sm">
                    Type a message...
                  </div>
                  <div className="h-10 w-10 rounded-full bg-primary/20 flex items-center justify-center text-primary">
                    <ArrowRight className="w-4 h-4" />
                  </div>
                </div>
                {}
                <div className="absolute bottom-2 left-1/2 -translate-x-1/2 w-32 h-1 bg-white/20 rounded-full" />
              </div>

            </div>

            {}
            <div className="absolute top-1/3 -right-12 md:-right-20 bg-card/80 backdrop-blur-xl border border-white/10 p-4 rounded-2xl shadow-2xl animate-pulse hidden lg:block max-w-[200px]">
              <div className="flex items-center gap-3 mb-2">
                <div className="p-2 bg-green-500/20 rounded-lg text-green-500">
                  <Check className="w-4 h-4" />
                </div>
                <div>
                  <p className="text-xs font-medium text-white">Booking Confirmed</p>
                  <p className="text-[10px] text-muted-foreground">Just now</p>
                </div>
              </div>
              <div className="h-1 w-full bg-white/10 rounded-full overflow-hidden">
                <div className="h-full w-full bg-green-500 animate-[shimmer_2s_infinite]" />
              </div>
            </div>

          </div>
        </div>
      </div>
    </section>;
}